import { ApiError, fetchWithAuth, readBody } from "./client";

// Wraps POST /api/v1/search/ai-suggest — the wizard's "Ask AI" button.
//
// The portal route is the only place the OpenAI API key is ever read;
// this client just shuttles `intent` (+ optional `current` query)
// across and surfaces the model's structured response.

export interface AiSuggestRequest {
  intent: string;
  current?: string;
}

export interface AiSuggestResponse {
  query: string;
  explanation: string;
}

// Distinct error class so the dialog can render the model's offending
// query alongside the validator's complaint when the model produces a
// shape we still reject. Falls back to the generic ApiError for 4xx /
// 5xx with no structured body.
export class AiSuggestInvalidError extends Error {
  query: string;
  explanation: string;
  constructor(message: string, query: string, explanation: string) {
    super(message);
    this.name = "AiSuggestInvalidError";
    this.query = query;
    this.explanation = explanation;
  }
}

export async function suggestSearchQuery(
  req: AiSuggestRequest,
): Promise<AiSuggestResponse> {
  const res = await fetchWithAuth("/api/v1/search/ai-suggest", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  if (res.status === 422) {
    // The model produced something our validator rejected — surface
    // both the message and the offending query so the dialog can
    // show what was attempted instead of just "request failed".
    const body = (await readBody(res)) as
      | { detail?: { message?: string; query?: string; explanation?: string } }
      | null;
    const detail = body?.detail ?? {};
    throw new AiSuggestInvalidError(
      detail.message ?? "AI produced an invalid query",
      detail.query ?? "",
      detail.explanation ?? "",
    );
  }
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `/api/v1/search/ai-suggest returned ${res.status}`,
    );
  }
  return (await res.json()) as AiSuggestResponse;
}
